<template>
  <div class="mt-4">
    <fragment>
      <page-head
        small
        :back-route-path="'/bankruptcy'"
        show-back
        :back-text="local.common.return"
        class="page-head"
      >
        <div class="head-wings">
          <div
            v-show="$vuetify.breakpoint.lgAndUp"
            class="pr-16"
          >
            <p class="wing-1">
              {{ pageHeaderTitle }}
              <span
                :class="{
                  'status-ok': requestInfo.requestStep !== mainRequestStep.rejected,
                  'status-rejected': requestInfo.requestStep === mainRequestStep.rejected,
                }"
                class="px-2"
              >
                <v-icon
                  small
                  :class="{
                    'status-ok': requestInfo.requestStep !== mainRequestStep.rejected,
                    'status-rejected': requestInfo.requestStep === mainRequestStep.rejected,
                  }"
                >
                  mdi-circle
                </v-icon>
                <span class="statusName">
                  {{ requestInfo.requestStatusName }}
                </span>
              </span>
            </p>
          </div>
          <div :class="['wing r-info']">
            <p class="wing-2">
              <span class="px-2"> {{ local.bankruptcy.bankruptcyFile.requestCode }} {{ requestInfo.code }}</span>
              <span class="px-2">
                {{ local.bankruptcy.bankruptcyFile.requestDate }} {{ toGeorgianDateString(requestInfo.creationDate) }}</span>
            </p>
          </div>
        </div>
      </page-head>
      <page-content
        small
        class="content-box"
      >
        <div class="content-border row">
          <div class="col-2 tabs-box">
            <v-tabs
              v-model="tab"
              vertical
              hide-slider
            >
              <v-tab
                v-for="navTab in tabs"
                :key="navTab.href"
                :disabled="navTab.disabled"
                :href="navTab.href"
                class="tab-button"
                active-class="tab-active"
              >
                <v-icon
                  left
                  class="tab-button-icon"
                >
                  {{ navTab.icon }}
                </v-icon>
                <span class="mr-1"> {{ navTab.title }}</span>
              </v-tab>
            </v-tabs>
          </div>
          <div class="col-10 p-2 tabs-section">
            <div
              v-if="loading"
              class="d-flex"
            >
              <v-col cols="12">
                <v-sheet
                  v-if="loading"
                  :color="'grey lighten-4'"
                  class="pa-3"
                >
                  <v-skeleton-loader type="card-heading, table-row-divider">
                  </v-skeleton-loader>
                </v-sheet>
              </v-col>
            </div>
            <template v-else>
              <request-summary
                :request-info="requestInfo"
                :procedures="procedures"
              ></request-summary>
              <v-tabs-items v-model="tab">
                <v-tab-item
                  v-if="tab == 'tab-1'"
                  value="tab-1"
                >
                  <basic-info-tab
                    :request-object-key="requestObjectKey"
                    :request-info="requestInfo"
                  ></basic-info-tab>
                </v-tab-item>
                <v-tab-item
                  v-if="tab == 'tab-2'"
                  value="tab-2"
                >
                  <main-request-details-tab :request-object-key="requestObjectKey"></main-request-details-tab>
                </v-tab-item>
                <v-tab-item
                  v-if="tab == 'tab-3'"
                  value="tab-3"
                >
                  <sessions-tab :request-object-key="requestObjectKey"></sessions-tab>
                </v-tab-item>
                <v-tab-item
                  v-if="tab == 'tab-4'"
                  value="tab-4"
                >
                  <judgements-tab
                    :request-object-key="requestObjectKey"
                    :request-info="requestInfo"
                    :show-new-request="showNewRequest"
                    :is-main-request-secretaries-approved="isMainRequestSecretariesApproved"
                  >
                  </judgements-tab>
                </v-tab-item>
                <v-tab-item
                  v-if="tab == 'tab-5'"
                  value="tab-5"
                >
                  <requests-tab
                    :request-object-key="requestObjectKey"
                    :request-info="requestInfo"
                    :show-new-request="showNewRequest"
                  ></requests-tab>
                </v-tab-item>
                <v-tab-item
                  v-if="tab == 'tab-6'"
                  value="tab-6"
                >
                  <decisions-tab :request-object-key="requestObjectKey"></decisions-tab>
                </v-tab-item>
                <v-tab-item
                  v-if="tab == 'tab-7'"
                  value="tab-7"
                >
                  <attachments-tab :request-object-key="requestObjectKey"></attachments-tab>
                </v-tab-item>
                <v-tab-item
                  v-if="tab == 'tab-8'"
                  value="tab-8"
                >
                  <deposits-tab :request-object-key="requestObjectKey">
                  </deposits-tab>
                </v-tab-item>
                <v-tab-item
                  v-if="tab == 'tab-9'"
                  value="tab-9"
                >
                  <reply-memos-tab
                    :request-object-key="requestObjectKey"
                    :request-info="requestInfo"
                    :show-new-request="showNewRequest"
                  >
                  </reply-memos-tab>
                </v-tab-item>
              </v-tabs-items>
            </template>
          </div>
        </div>
      </page-content>
    </fragment>
  </div>
</template>
<script>
import { createNamespacedHelpers } from 'vuex';
const { mapActions, mapState } = createNamespacedHelpers('BankruptcyModule');
import BasicInfoTab from './basic-info-tab/index';
import MainRequestDetailsTab from './main-request-details-tab/index.vue';
import SessionsTab from './sessions-tab/index';
import JudgementsTab from './judgements-tab/index';
import RequestsTab from './requests-tab/index';
import DecisionsTab from './decisions-tab/index';
import AttachmentsTab from './attachments-tab/index';
import RequestSummary from './request-summary.vue';
import { toGeorgianDateString } from '@/helpers/utils';
import { requestOwnerTypeEnum, mainRequestStep, mainRequestCategoryEnum, requestStatusEnum } from '@/modules/bankruptcy/helpers/enum';
const { mapActions: mapGlobalActions } = createNamespacedHelpers('GlobalModule');
import { userType } from '@/helpers/enum';
import DepositsTab from './deposits-tab/index';
import ReplyMemosTab from './reply-memos-tab/index';

export default {
  name: 'Bankruptcy',
  components: {
    BasicInfoTab,
    MainRequestDetailsTab,
    SessionsTab,
    JudgementsTab,
    RequestsTab,
    DecisionsTab,
    AttachmentsTab,
    RequestSummary,
    DepositsTab,
    ReplyMemosTab,
  },
  data() {
    return {
      loading: false,
      requestInfo: {},
      procedures: [],
      tab: {},
      toGeorgianDateString,
      requestOwnerTypeEnum,
      mainRequestStep,
      userType,
      mainRequestCategoryEnum,
      showReplyMemoTab: false,
      requestStatusEnum,
      mainRequestSecretariesApprovedIdentityNumber: null,
    };
  },
  computed: {
    ...mapState({ user: state => state.currentUser }),
    requestObjectKey() {
      return this.$route.params.objectKey;
    },
    tabs() {
      return [
        {
          href: '#tab-1',
          title: this.local.bankruptcy.bankruptcyFile.basicInfo.tabTitle,
          icon: 'mdi-file-table',
          disabled: false,
          isEnabled: true,
        },
        {
          href: '#tab-2',
          title: this.local.bankruptcy.bankruptcyFile.requestDetails.tabTitle,
          icon: 'mdi-database',
          disabled: false,
          isEnabled: this.requestInfo.requestOwnerTypeId === requestOwnerTypeEnum.debtor,
        },
        {
          href: '#tab-3',
          title: this.local.bankruptcy.bankruptcyFile.sessions.tabTitle,
          icon: 'mdi-account-group',
          disabled: false,
          isEnabled: this.requestInfo.categoryId != mainRequestCategoryEnum.deposit,
        },
        {
          href: '#tab-4',
          title: this.local.bankruptcy.bankruptcyFile.judgements.tabTitle,
          icon: 'mdi-gavel',
          disabled: false,
          isEnabled: this.requestInfo.categoryId != mainRequestCategoryEnum.deposit,
        },
        {
          href: '#tab-5',
          title: this.local.bankruptcy.bankruptcyFile.requests.tabTitle,
          icon: 'mdi-archive',
          isEnabled: true,
        },
        {
          href: '#tab-6',
          title: this.local.bankruptcy.bankruptcyFile.decisions.tabTitle,
          icon: 'mdi-bookshelf',
          disabled: false,
          isEnabled: true,
        },
        {
          href: '#tab-7',
          title: this.local.bankruptcy.bankruptcyFile.attachments.tabTitle,
          icon: 'mdi-file-document',
          disabled: false,
          isEnabled: true,
        },
        {
          href: '#tab-8',
          title: this.local.bankruptcy.bankruptcyFile.deposits.tabTitle,
          icon: 'mdi-file-document',
          disabled: false,
          isEnabled: this.requestInfo.categoryId == mainRequestCategoryEnum.deposit,
        },
        {
          href: '#tab-9',
          title: this.local.bankruptcy.bankruptcyFile.replyMemos.tabTitle,
          icon: 'mdi-archive',
          disabled: false,
          isEnabled: this.showReplyMemoTab,
        },
      ].filter(t => t.isEnabled);
    },
    pageHeaderTitle() {
      if (!this.requestInfo.code) return '..';
      return `${this.local.bankruptcy.bankruptcyFile.request} ${this.requestInfo.requestCategoryName} ${this.requestInfo.procedureName}`;
    },
    showNewRequest() {
      return this.user && (this.user.isIndividualUser || this.user.loginType == 'governmental' || this.user.userType == this.userType.najizLawyer || this.user.isOrganizationUser);
    },
    isMainRequestSecretariesApproved() {
      return this.user.socialId == this.mainRequestSecretariesApprovedIdentityNumber;
    },
  },
  watch: {},
  mounted() {
    this.loading = true;
    Promise.all([
      this.getMainRequestInfo(this.requestObjectKey),
      this.getProceduresByRequestObjectKey(this.requestObjectKey),
      this.getBankruptcyUserInfo(),
      this.getMainRequestParties(this.requestObjectKey),
      this.getMainRequestSecretariesApproved(this.requestObjectKey),
    ])
      .then((data) => {
        this.requestInfo = data[0].data;
        this.procedures = data[1].data;
        let parties = data[3].data;
        if (this.requestInfo.requestOwnerTypeId === requestOwnerTypeEnum.creditor && this.requestInfo.statusId == requestStatusEnum.transferredToCircuit) {
          this.showReplyMemoTab = this.showNewRequest && (parties.debtor.identityNumber === this.user.socialId || (parties.isCurrentUserDebtorRepresentative));
        }
        this.mainRequestSecretariesApprovedIdentityNumber = data[4].data.map(d => d.identityNumber).find(id => id == this.user.socialId);
      })
      .catch(err => this.toaster.error(err.message))
      .finally(() => (this.loading = false));
  },
  methods: {
    ...mapActions([
      'getMainRequestInfo',
      'getProceduresByRequestObjectKey',
      'getBankruptcyUserInfo',
      'getMainRequestParties',
      'getMainRequestSecretariesApproved',
    ]),
    ...mapGlobalActions(['getUserInfo']),
  },
};
</script>

<style lang="scss" scoped>
.content-border {
  border: 35px solid #faf9f4 !important;
}
.head-wings {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 25px;
  .r-info {
    margin-left: 40px;
  }
}
.tabs-box {
  padding: 0;
  background: #faf9f4;

  .tab-button {
    font: normal normal normal 16px/24px Almarai;
    justify-content: right;

    &.tab-active {
      border-right: 4px solid var(--app-primary-color);
      font-weight: bold;
      color: var(--app-primary-color);
    }
  }
}
.wing-1 {
  font-weight: 1000;
  font-size: 18px;
  padding: 155px;
}
.wing-2 {
  font-weight: bolder;
  font-size: 14px;
}
.tabs-section {
  min-height: 600px;
}

.status-rejected {
  color: #f44336 !important;
}

.status-ok {
  color: #29af5b;
}

.v-tab {
  background: #faf9f4;
}
.statusName {
  font-size: 14px;
}
.page-head {
  box-shadow: none !important;
  background: #fff !important;
}
.content-box {
  padding: 0px !important;
  margin-top: 0px !important;
}
</style>
